import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'

import { CAPACITY_UNITS, UPDATE_FREQUENCY } from 'modules/attendance/lib/enums'

interface ITimeoffCompensation {
  id: string
  compensation_enabled: boolean | string
  frequency: UPDATE_FREQUENCY
  has_threshold_enabled: boolean
  threshold_capacity: number | null
  threshold_units: CAPACITY_UNITS
  compensation_capacity: number | null
  compensation_units: CAPACITY_UNITS
  has_unlimited_compensation: boolean
  negative_transfers: string
  attendance_employees_setting_id: string
}

class TimeoffCompensation extends Model<ITimeoffCompensation> {
  static urlRoot() {
    return '/attendance/configure_compensation'
  }

  constructor(attr: ITimeoffCompensation) {
    super(attr)

    makeObservable(this, {
      isDefault: computed,
      isEnabled: computed,
      withLimits: computed,
      withStartLimitOnly: computed,
      withoutLimits: computed,
      thresholdCapacity: computed,
      compensationCapacity: computed,
      thresholdUnitsInHours: computed,
      compensationUnitsInHours: computed,
    })
  }

  get isDefault(): boolean {
    return this.attributes.id.includes('default_')
  }

  get isEnabled(): boolean {
    return !!this.attributes.compensation_enabled
  }

  get withLimits(): boolean {
    return !!(
      this.attributes.has_threshold_enabled &&
      this.attributes.threshold_capacity &&
      !this.attributes.has_unlimited_compensation
    )
  }

  get withStartLimitOnly(): boolean {
    return !!(
      this.attributes.has_threshold_enabled &&
      this.attributes.threshold_capacity &&
      this.attributes.has_unlimited_compensation
    )
  }

  get withEndLimitOnly(): boolean {
    return !this.attributes.has_threshold_enabled && !this.attributes.has_unlimited_compensation
  }

  get withoutLimits(): boolean {
    return !!(!this.attributes.has_threshold_enabled && this.attributes.has_unlimited_compensation)
  }

  get thresholdCapacity(): number | null {
    return this.attributes.threshold_capacity
  }

  get compensationCapacity(): number | null {
    return this.attributes.compensation_capacity
  }

  get thresholdUnitsInHours(): boolean {
    return this.attributes.threshold_units === CAPACITY_UNITS.hours
  }

  get compensationUnitsInHours(): boolean {
    return this.attributes.compensation_units === CAPACITY_UNITS.hours
  }

  get compensationEnabled(): boolean {
    return !!this.attributes.compensation_enabled
  }

  get frequency(): string {
    return this.attributes.frequency
  }

  get frequencyMonthly(): boolean {
    return this.frequency === UPDATE_FREQUENCY.monthly
  }

  get frequencyWeekly(): boolean {
    return this.frequency === UPDATE_FREQUENCY.weekly
  }
}

export default TimeoffCompensation
